'use client'

import {useLayoutStore} from '../provider'
import {ChevronDown, Heart, Mail, Phone, User} from 'lucide-react'
import {observer} from 'mobx-react'
import React, {useEffect, useRef} from 'react'

import {Routes} from '@/src/config/routes'

import iconLocation from '@/src/shared/assets/media/icon-location.svg'
import IconBasket from '@/src/shared/ui/icons/basket'
import {Image} from '@/src/shared/ui/image'
import {BasketCountContainer} from '@/src/shared/ui/layout/ui/header/basket-count'
import {FavouriteCountContainer} from '@/src/shared/ui/layout/ui/header/favourite-count'
import Logo from '@/src/shared/ui/logo'
import {NextLink} from '@/src/shared/ui/next-link'
import {fillNavLink} from '@/src/shared/utils/functions'

import * as UI from './header.styles'
import {HiddenNav} from './hidden-nav'
import {MobileBurger} from './mobile-burger'
import HeaderMobileLinks from './mobile-links'
import {HeaderSearch} from '@/src/shared/ui/layout'
import {useRouter} from 'nextjs-toploader/app'
import {CatalogDeepMenu} from '@/src/features/catalog/deep-menu'
import {DeepMenu} from '@/src/features/deep-menu'
import {formatMenuItemsDeep} from '@/src/core/domain/page/page-link-url'
import {getInjection} from '@runlab/di'

export const Header = observer(() => {
  const ref = useRef<Nullable<HTMLDivElement>>(null)
  const layoutStore = useLayoutStore()
  const router = useRouter()

  useEffect(() => {
    if (ref.current) {
      layoutStore.setHeaderHeight(ref.current.offsetHeight)
    }
  }, [])

  const topHeaderMenu = (layoutStore.data.header.menu.header_top ?? [])
    .map(item => ({...item, url: Routes.static(item.url, layoutStore.data.static_pages ?? [])}))
    .map((item, index) => fillNavLink(item, index, 'header-menu'))

  const onClickTel = (phone: string) => {
    getInjection('PulseAnalytics').track('phone_click', { phone })
  }

  const onClickMail = (email: string) => {
    getInjection('PulseAnalytics').track('email_click', { email })
  }

  return (
    <>
      <UI.Header>
        <UI.HeaderTop data-header="" data-testid="header-top" ref={ref}>
          <UI.Container>
            <MobileBurger
              dark
              open={layoutStore.ui.mobile.menu.opened}
              onClick={open => layoutStore.setMobileOpened(!open)}
            />
            <UI.LogoWrapper>
              <Logo src={layoutStore.data.header.logo.src} />
            </UI.LogoWrapper>
            <UI.TopContainer>
              <UI.NavWrapper>
                <CatalogDeepMenu />

                {layoutStore.data.header.promo_button && (
                  <UI.SaleButton
                    outlined
                    primary
                    data-dark="true"
                    href={layoutStore.data.header.promo_button.link}>
                    {layoutStore.data.header.promo_button.title}
                  </UI.SaleButton>
                )}

                <UI.NavMenu className="header-nav">
                  <DeepMenu
                    items={formatMenuItemsDeep(
                      layoutStore.data.header.menu.header_top ?? [],
                      layoutStore.data.static_pages ?? [],
                    )}>
                    {(ref, open, setOpen) => (
                      <UI.NavBurgerButton
                        ref={ref}
                        outlined
                        dark
                        open={open}
                        onChange={burgerOpened => setOpen(burgerOpened)}>
                        Меню
                      </UI.NavBurgerButton>
                    )}
                  </DeepMenu>

                  <UI.NavItems>
                    <HiddenNav items={topHeaderMenu} />
                  </UI.NavItems>
                </UI.NavMenu>
              </UI.NavWrapper>
              <UI.ShopContainer>
                <UI.ShopLinks>
                  <UI.SearchLink>
                    <HeaderSearch />
                  </UI.SearchLink>
                  <UI.ProfileLink as={NextLink} title="Профиль" href="/profile/">
                    <User />
                  </UI.ProfileLink>

                  <UI.WishLink
                    as={NextLink}
                    title="Избранное"
                    href={Routes.favourite.page}
                    onClick={e => {
                      if (layoutStore.data.session?.user?.id) {
                        e.preventDefault()
                        router.push(Routes.profile.favourite.page)
                      }
                    }}>
                    <Heart />
                    <UI.FavouriteCount as={FavouriteCountContainer} />
                  </UI.WishLink>
                  <UI.BasketLink as={NextLink} title="Корзина" href={Routes.basket.page}>
                    <IconBasket />
                    <UI.BasketCount as={BasketCountContainer} />
                  </UI.BasketLink>
                </UI.ShopLinks>
                <UI.ContactsButtonMobile>
                  <UI.IconBtn>
                    <HeaderSearch />
                  </UI.IconBtn>
                  {layoutStore.data.header.phone && (
                    <UI.IconLink href={`tel:${layoutStore.data.header.phone}`} onClick={() => onClickTel(layoutStore.data.header.phone!)}>
                      <Phone />
                    </UI.IconLink>
                  )}
                  {layoutStore.data.header.email && (
                    <UI.IconLink href={`mailto:${layoutStore.data.header.email}`} onClick={() => onClickMail(layoutStore.data.header.email!)}>
                      <Mail />
                    </UI.IconLink>
                  )}
                </UI.ContactsButtonMobile>
              </UI.ShopContainer>
            </UI.TopContainer>
          </UI.Container>
        </UI.HeaderTop>
        <UI.HeaderBottom data-testid="header-bottom">
          <UI.Container>
            <UI.Links>
              <UI.LinksContainer>
                {(layoutStore.data.header.menu.header_bottom ?? []).map((menuItem, i) => (
                  <UI.Link
                    key={i}
                    href={Routes.static(menuItem.url, layoutStore.data.static_pages ?? [])}>
                    {menuItem.title}
                  </UI.Link>
                ))}
                <UI.ShopLocationsLink href={Routes.contacts.page} asChild>
                  <Image src={iconLocation} width={15} height={18} alt="" />
                  <span>Магазины</span>
                </UI.ShopLocationsLink>
              </UI.LinksContainer>
              <UI.InfoButton outlined>
                <span>Информация</span>
                <ChevronDown />
              </UI.InfoButton>
            </UI.Links>
          </UI.Container>
        </UI.HeaderBottom>
      </UI.Header>
      <HeaderMobileLinks />
    </>
  )
})
